
import throttle from 'lodash.throttle';
import moment from 'moment';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    Input,
    Popup,
    Button,
    Image,
    List,
    Label,
    Container,
    Segment,
    Icon,
    Header,
    Divider, // Added Divider for visual separation
} from 'semantic-ui-react';
import { useQuery, useMutation } from '@apollo/client';
import { useCallback, useState, useEffect } from 'react';

// Local imports
import { PATHS } from '../../../../paths';
import { formatDate } from '../../../../dates';
import { useIsAdmin } from '../../../../hooks/isAdmin';
import { useIsCenterManager } from '../../../../hooks/isCenterManager';

import { ResponsiveContainer } from '../../../../components/ResponsiveContainer';
import { InterimStates } from '../../../../components/InterimStates';
import { PersonCourseAttendance } from '../../../../components/Protected/PersonCourseAttendance';
import { PersonBlockAttendance } from '../../../../components/Protected/PersonBlockAttendance';
import { PersonTags } from '../../../../components/Protected/PersonTags';
import { PermissionEditor } from '../../../../components/Protected/PermissionEditor';

// --- GraphQL Queries and Mutations ---
import { UPDATE_USER_FIELD } from '../../../../mutations/UPDATE_USER_FIELDS'
import { DELETE_USER_RELATIONSHIP } from '../../../../mutations/DELETE_USER_RELATIONSHIP';
import { CREATE_SIBLING_RELATIONSHIP } from '../../../../mutations/CREATE_SIBLING_RELATIONSHIP';
import { CREATE_PARENT_RELATIONSHIP } from '../../../../mutations/CREATE_PARENT_RELATIONSHIP';
import { ADMIN_GET_PERSON } from '../data/ADMIN_GET_PERSON';
import { UserPhoneNumber } from '../../../../components/UserPhoneNumber';

import { sortBlocksDecending } from '../utils/sortBlocksDecending';

const AdminProfileView = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isAdmin = useIsAdmin();
    const isCenterManager = useIsCenterManager();

    // State for editable fields
    const [editMode, setEditMode] = useState(false);
    const [name, setName] = useState('');
    const [scriptName, setScriptName] = useState('');
    const [address, setAddress] = useState('');

    // State for photo upload
    const [file, setFile] = useState<File | null>(null);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [showLargePhoto, setShowLargePhoto] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setCopied(true);

            // Reset the "Copied" state after 2 seconds
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    // GraphQL hooks
    const { loading, error, data, refetch } = useQuery(ADMIN_GET_PERSON, {
        variables: { personId: Number(id) },
        skip: !id || isNaN(Number(id)),
    });

    const [updateUser] = useMutation(UPDATE_USER_FIELD);
    const [deleteUserRelationship] = useMutation(DELETE_USER_RELATIONSHIP);
    const [createSiblingRelationship] = useMutation(CREATE_SIBLING_RELATIONSHIP);
    const [createParentRelationship] = useMutation(CREATE_PARENT_RELATIONSHIP);

    // Effect to initialize editable fields when data loads or changes
    useEffect(() => {
        if (data?.user) {
            setName(data.user.name || '');
            setScriptName(data.user.scriptName || '');
            setAddress(data.user.address || '');
        }
    }, [data]);

    // --- Throttled Update Functions ---

    const CreateThrottledUpdater = (field: string) =>
        useCallback(
            throttle(
                async (value: string) => {
                    await updateUser({
                        variables: {
                            userId: Number(id),
                            value,
                            field,
                        },
                    });
                    refetch();
                },
                500,
                { leading: false, trailing: true }
            ),
            [updateUser, id, refetch]
        );

    const updateName = CreateThrottledUpdater('name');
    const updateScriptName = CreateThrottledUpdater('scriptName');
    const updateAddress = CreateThrottledUpdater('address');

    const archiveUser = useCallback(
        throttle(
            async () => {
                if (window.confirm('Are you sure you want to archive this person?')) {
                    await updateUser({
                        variables: {
                            userId: Number(id),
                            value: 'now',
                            field: 'archivedAt',
                        },
                    });
                    navigate('/people');
                }
            },
            500,
            { leading: false, trailing: true }
        ),
        [updateUser, id, navigate]
    );

    // --- File Upload Logic ---

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && event.target.files[0]) {
            setFile(event.target.files[0]);
        }
    };

    const uploadProfilePhoto = async () => {
        if (!file || !data?.user?.uploadURLSmall) {
            console.error('No file selected or upload URL missing.');
            return;
        }
        setUploadingFile(true);
        try {
            await fetch(data.user.uploadURLSmall, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': 'application/octet-stream',
                    'Content-Length': file.size.toString(),
                },
                referrer: 'no-referrer',
            });
            console.log('File uploaded successfully');
            setFile(null);
            refetch();
        } catch (uploadError) {
            console.error('Upload failed:', uploadError);
        } finally {
            setUploadingFile(false);
        }
    };

    // --- Render Logic ---

    if (loading || error || !data?.user) {
        return (
            <InterimStates
                loading={loading}
                error={error}
                label="person's details"
            />
        );
    }

    const { user } = data;
    const canEdit = editMode && (isAdmin || isCenterManager);


    if (user.id == null) {
        return (
            <div>You don't have permission to view this</div>
        )
    }

    return (
        <ResponsiveContainer>
            <Segment
                style={{ padding: '2em 0em', backgroundColor: '#f5f5f5' }} // Light background for the overall segment
                vertical
            >
                <Container style={{ maxWidth: '800px' }}>
                    {/* Back Button and Edit Mode Toggle */}
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1em',
                        }}
                    >
                        <Icon
                            name="arrow left"
                            size="large"
                            style={{ cursor: 'pointer' }}
                            onClick={() => navigate(-1)}
                        />

                        <div>
                            {user && user.records && user.records.length > 0 && (
                                <Button
                                    basic
                                    color="green"
                                    as={Link}
                                    to={`${PATHS.PEOPLE}/${user.id}/records`}
                                >
                                    <Icon name="file alternate" />
                                    View records
                                </Button>
                            )}
                            {(isAdmin || isCenterManager) && !user.isArchived && (
                                <Button
                                    basic
                                    color="blue"
                                    onClick={() => {
                                        if (!editMode) {
                                            setName(user.name || '');
                                            setAddress(user.address || '');
                                            setScriptName(user.scriptName || '');
                                        }
                                        setEditMode(!editMode);
                                    }}
                                >
                                    <Icon name="edit" />
                                    {editMode ? 'Exit Edit Mode' : 'Edit Profile'}
                                </Button>
                            )}
                        </div>
                    </div>

                    {user.isArchived && (
                        <Label color="red" size="large" style={{ marginBottom: '1em' }}>
                            Archived Profile
                        </Label>
                    )}

                    {/* --- Profile Header Section --- */}
                    <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            {/* Photo Area */}
                            <div style={{ marginRight: '2em' }}>
                                {showLargePhoto ? (
                                    <Container textAlign="center">
                                        <Image
                                            src={user.photoUrl}
                                            size="large"
                                            circular // Circular for profile photos
                                            style={{
                                                width: '380px',
                                                height: '380px',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                border: '2px solid #ddd',
                                            }}
                                            onClick={() => setShowLargePhoto(false)}
                                        />
                                        {canEdit && (
                                            <div style={{ marginTop: '1em' }}>
                                                {uploadingFile ? (
                                                    <Label basic loading>
                                                        Uploading...
                                                    </Label>
                                                ) : (
                                                    <Label basic>Click photo to minimize</Label>
                                                )}
                                                <input type="file" onChange={handleFileChange} />
                                                <Button
                                                    compact
                                                    primary
                                                    size="tiny"
                                                    onClick={uploadProfilePhoto}
                                                    disabled={!file}
                                                    style={{ marginTop: '0.5em' }}
                                                >
                                                    Upload New Photo
                                                </Button>
                                            </div>
                                        )}
                                    </Container>
                                ) : (
                                    <>
                                        <Image
                                            src={user.photoUrl}
                                            size="tiny"
                                            circular // Circular for profile photos
                                            style={{
                                                width: '120px',
                                                height: '120px',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                border: '2px solid #ddd',
                                            }}
                                            onClick={() => setShowLargePhoto(true)}
                                        />
                                        {canEdit && (
                                            <div style={{ marginTop: '0.5em' }}>
                                                {uploadingFile ? (
                                                    <Label basic loading>
                                                        Uploading...
                                                    </Label>
                                                ) : (
                                                    <Label basic>Click photo to enlarge</Label>
                                                )}
                                                <input
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    style={{ width: '100%' }}
                                                />
                                                <Button
                                                    compact
                                                    primary
                                                    size="tiny"
                                                    onClick={uploadProfilePhoto}
                                                    disabled={!file}
                                                    style={{ marginTop: '0.5em' }}
                                                >
                                                    Upload Photo
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Name and Basic Info */}
                            <div>
                                <Header as="h1" style={{ marginBottom: '0.2em' }}>
                                    {canEdit ? (
                                        <Input
                                            fluid
                                            placeholder="Full Name"
                                            value={name}
                                            onChange={(e) => {
                                                setName(e.target.value);
                                                updateName(e.target.value);
                                            }}
                                        />
                                    ) : (
                                        user.name
                                    )}
                                </Header>

                                {(user.scriptName || user.preferredName) && (
                                    <Header as="h3" color="grey" style={{ marginTop: '0' }}>
                                        {canEdit ? (
                                            <Input
                                                fluid
                                                placeholder="Script Name (e.g., Arabic, Chinese)"
                                                value={scriptName}
                                                onChange={(e) => {
                                                    setScriptName(e.target.value);
                                                    updateScriptName(e.target.value);
                                                }}
                                            />
                                        ) : (
                                            <>
                                                {user.scriptName}{' '}
                                                {user.preferredName && `(${user.preferredName})`}
                                            </>
                                        )}
                                    </Header>
                                )}
                                <p style={{ color: '#555', fontSize: '1.1em' }}>
                                    <Icon name="id card outline" /> Identifier: {user.identifier}
                                </p>
                            </div>
                            <div style={{ marginLeft: 'auto' }}>
                                <Popup
                                    content='Copied profile URL to clipboard!'
                                    open={copied}
                                    on='click'
                                    trigger={
                                        <Button
                                            color={copied ? 'green' : 'teal'}
                                            icon={copied ? 'check' : 'copy'}
                                            content={copied ? 'Copied' : 'Share profile'}
                                            onClick={handleCopy}
                                        />
                                    }
                                />
                            </div>
                        </div>
                    </Segment>


                    {canEdit && (
                        <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                            <Header as="h2">
                                <Icon name="users" /> Relationships
                            </Header>
                            <Divider />
                            Add sibling <input type="text" placeholder="Sibling Identifier" id="siblingIdentifier" />{' '}
                            <Button onClick={async () => {
                                const siblingIdentifierInput = (document.getElementById('siblingIdentifier') as HTMLInputElement);
                                const siblingIdentifier = siblingIdentifierInput.value;
                                if (!siblingIdentifier) {
                                    return;
                                }
                                await createSiblingRelationship({
                                    variables: {
                                        newSiblingIdentifier: siblingIdentifier,
                                        siblingUserId: Number(id),
                                    },
                                });
                                siblingIdentifierInput.value = '';
                                refetch();
                            }
                            }>Add Sibling</Button>
                            <br /><br />
                            Add parent <input type="text" placeholder="Parent Identifier" id="parentIdentifier" />{' '}
                            <Button onClick={async () => {
                                const parentIdentifierInput = (document.getElementById('parentIdentifier') as HTMLInputElement);
                                const parentIdentifier = parentIdentifierInput.value;
                                if (!parentIdentifier) {
                                    return;
                                }
                                await createParentRelationship({
                                    variables: {
                                        newParentIdentifier: parentIdentifier,
                                        childUserId: Number(id),
                                    },
                                });
                                parentIdentifierInput.value = '';
                                refetch();
                            }}>Add Parent</Button>
                        </Segment>
                    )}

                    {/* --- About Section --- */}
                    <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                        <Header as="h2">
                            <Icon name="info circle" /> About
                        </Header>
                        <Divider />
                        <List relaxed divided>
                            {user.buddies.length > 0 &&
                                <List.Item>
                                    <Label horizontal style={{ minWidth: '120px' }}>
                                        <Icon name="handshake outline" /> Buddy
                                    </Label>
                                    {user.buddies.map((b: any) => (
                                        <>
                                            <Image
                                                src='https://m.media-amazon.com/images/M/MV5BOTliYmQ4ZDQtMGQyOC00NDllLThmNjAtYmU3YzhjZWQ1ZWNmXkEyXkFqcGc@._V1_FMjpg_UX2160_.jpg'
                                                size="tiny"
                                                circular // Circular for profile photos
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    border: '2px solid #ddd',
                                                }}
                                            />
                                            {b.name}
                                        </>
                                    ))}
                                </List.Item>
                            }
                            {user.parents.length > 0 &&
                                <List.Item>
                                    <Label horizontal style={{ minWidth: '120px' }}>
                                        <Icon name="child" /> Parents

                                    </Label>
                                    {user.parents.map((b: any) => (
                                        <div style={{ position: 'relative', display: 'inline-block', padding: '5px' }}>
                                            {editMode && <Icon
                                                name="times circle" // or just "close"
                                                color="red"
                                                link
                                                onClick={() => deleteUserRelationship({
                                                    variables: {
                                                        userId1: Number(id),
                                                        userId2: Number(b.id),
                                                        relationshipType: 'PARENT',
                                                    },
                                                }).then(() => refetch())}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    zIndex: 1,
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%'
                                                }}
                                            />}
                                            <Image
                                                src={b.photoUrl}
                                                size="tiny"
                                                circular // Circular for profile photos
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    border: '2px solid #ddd',
                                                }}
                                            />
                                            <Link to={`/people/${b.id}`}>
                                                {b.name}
                                            </Link>
                                        </div>
                                    ))}
                                </List.Item>
                            }
                            {user.siblings.length > 0 &&
                                <List.Item>
                                    <Label horizontal style={{ minWidth: '120px' }}>
                                        <Icon name="group" /> Siblings
                                    </Label>
                                    {user.siblings.map((b: any) => (
                                        <div style={{ position: 'relative', display: 'inline-block', padding: '5px' }}>
                                            {editMode && <Icon
                                                name="times circle" // or just "close"
                                                color="red"
                                                link
                                                onClick={() => deleteUserRelationship({
                                                    variables: {
                                                        userId1: Number(id),
                                                        userId2: Number(b.id),
                                                        relationshipType: 'SIBLING',
                                                    },
                                                }).then(() => refetch())}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    zIndex: 1,
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%'
                                                }}
                                            />}
                                            <Image
                                                src={b.photoUrl}
                                                size="tiny"
                                                circular // Circular for profile photos
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    border: '2px solid #ddd',
                                                }}
                                            />
                                            <Link to={`/people/${b.id}`}>
                                                {b.name}
                                            </Link>
                                        </div>
                                    ))}
                                </List.Item>
                            }
                            {user.children.length > 0 &&
                                <List.Item>
                                    <Label horizontal style={{ minWidth: '120px' }}>
                                        <Icon name="group" /> Children
                                    </Label>
                                    {user.children.map((b: any) => (
                                        <div style={{ position: 'relative', display: 'inline-block', padding: '5px' }}>
                                            {editMode && <Icon
                                                name="times circle" // or just "close"
                                                color="red"
                                                link
                                                onClick={() => deleteUserRelationship({
                                                    variables: {
                                                        userId1: Number(id),
                                                        userId2: Number(b.id),
                                                        relationshipType: 'PARENT',
                                                    },
                                                }).then(() => refetch())}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-5px',
                                                    right: '-5px',
                                                    zIndex: 1,
                                                    backgroundColor: 'white',
                                                    borderRadius: '50%'
                                                }}
                                            />}
                                            <Image
                                                src={b.photoUrl}
                                                size="tiny"
                                                circular // Circular for profile photos
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    border: '2px solid #ddd',
                                                }}
                                            />
                                            <Link to={`/people/${b.id}`}>
                                                {b.name}
                                            </Link>
                                        </div>
                                    ))}
                                </List.Item>
                            }
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="home" /> Address
                                </Label>
                                {canEdit ? (
                                    <Input
                                        type="text"
                                        fluid
                                        value={address}
                                        onChange={(e) => {
                                            setAddress(e.target.value);
                                            updateAddress(e.target.value);
                                        }}
                                    />
                                ) : (
                                    user.address
                                )}
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="phone" /> Phone
                                </Label>
                                <UserPhoneNumber userId={user.id} inEditMode={editMode} />
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="birthday" /> Date of Birth
                                </Label>
                                {formatDate(user.dob)}
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="calendar outline" /> Age
                                </Label>
                                {moment().diff(moment(Number(user.dob)), 'years')} years,{' '}
                                {moment().diff(moment(Number(user.dob)), 'months') % 12} months
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="check" /> Attendance
                                </Label>
                                {Math.round(user.attendancePercentage * 100)}%
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="camera" /> Picture Permission
                                </Label>
                                {user.picturePermission ? 'Allowed' : 'Not Allowed'}
                            </List.Item>
                            <List.Item>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="bed" /> Truancy
                                </Label>
                                {user.truency}
                            </List.Item>
                            <List.Item disabled={!isAdmin}>
                                <Label horizontal style={{ minWidth: '120px' }}>
                                    <Icon name="file" /> Reg. Document
                                </Label>
                                {user.haveRegistrationDocument ? 'Yes ' : 'No '}
                                {user.registrationDocumentUrl && (
                                    <a
                                        target="_blank"
                                        href={user.registrationDocumentUrl}
                                        rel="noopener noreferrer"
                                    >
                                        - View
                                    </a>
                                )}
                            </List.Item>
                        </List>
                    </Segment>

                    {/* --- Tags Section (if in edit mode) --- */}
                    {canEdit && (
                        <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                            <Header as="h2">
                                <Icon name="tags" /> Profile Tags
                            </Header>
                            <Divider />
                            <PersonTags
                                tags={user.tags}
                                refetchPerson={() =>
                                    refetch({ variables: { personId: Number(id) } })
                                }
                            />
                        </Segment>
                    )}

                    {/* --- Current Block Attendance Section --- */}
                    <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                        <Header as="h2">
                            <Icon name="calendar check outline" /> Current Block Attendance
                        </Header>
                        <Divider />
                        {user.currentCourseAttendances.length === 0 ? (
                            <p>Not currently enrolled in any classes.</p>
                        ) : (
                            <List divided relaxed>
                                {user.currentCourseAttendances.filter(({ course }: any) => {
                                    return course && course.SKU.endsWith('APR2026');
                                })
                                    .map(({ course, attendance }: any) => (
                                        <List.Item key={course.id}>
                                            <List.Content>
                                                <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                                                    {course.name}
                                                </List.Header>
                                                <List.Description>
                                                    {course.overseers.length > 0 && course.overseers.map((t: any) => (
                                                        <>
                                                            <Image
                                                                avatar
                                                                src={t.photoUrl}
                                                                size="tiny"
                                                                style={{ width: '2em', height: '2em', objectFit: 'cover' }}
                                                            />
                                                            <Link to={`/people/${t.id}`}>
                                                                {t.name}
                                                            </Link>
                                                        </>
                                                    ))}{course.overseers.length > 0 && <br />}
                                                    {course.SKU} <br />
                                                    Attendance: {attendance.reduce((acc: number, a: any) => acc + (a.status === 'PRESENT' ? 1 : 0), 0)}/{attendance.length} <br />
                                                    {attendance.map((a: any) => (
                                                        <span key={a.id} style={{ marginRight: '0.5em' }}>
                                                            {a.status === 'ABSENT' ? '❌' : a.status === 'PRESENT' ? '✅' : '❓'}
                                                        </span>
                                                    ))}
                                                </List.Description>
                                            </List.Content>
                                        </List.Item>
                                    ))}
                            </List>
                        )}
                    </Segment>

                    {/* --- Classes Section --- */}
                    <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                        <Header as="h2">
                            <Icon name="book" /> Past Classes
                        </Header>
                        <Divider />
                        {user.courses
                            .length === 0 ? (
                            <p>Not currently enrolled in any classes.</p>
                        ) : (
                            <List divided relaxed>
                                {Object.entries(
                                    user.courses
                                        .reduce((acc: any, c: any) => {
                                            const sku = c.SKU.slice(-7);
                                            if (!acc[sku]) {
                                                acc[sku] = [];
                                            }
                                            acc[sku].push(c);
                                            return acc;
                                        }, {})
                                )
                                    .sort(sortBlocksDecending as any)
                                    .map(([sku, courses]: [string, any]) => (
                                        <List.Item key={sku}>
                                            <Header as="h4" style={{ marginBottom: '0.5em' }}>
                                                {sku}
                                            </Header>
                                            {courses.length > 1 && (<>
                                                Block Attendance:{' '}
                                                <PersonBlockAttendance
                                                    personId={Number(id)}
                                                    blockCode={sku}
                                                />
                                            </>
                                            )}
                                            <List.List>
                                                {courses.map((course: any) => (
                                                    <List.Item
                                                        key={course.id}>
                                                        <List.Content>
                                                            <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                                                                {course.name}
                                                            </List.Header>
                                                            <List.Description>
                                                                {course.SKU} <br />
                                                                Attendance:{' '}
                                                                <PersonCourseAttendance
                                                                    personId={Number(id)}
                                                                    courseId={course.id}
                                                                />
                                                            </List.Description>
                                                        </List.Content>
                                                    </List.Item>
                                                ))}
                                            </List.List>
                                        </List.Item>
                                    ))}
                            </List>
                        )}
                    </Segment>

                    {/* --- Teaching Section --- */}
                    {user.coursesTeaching.length > 0 && (
                        <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                            <Header as="h2">
                                <Icon name="graduation cap" /> Classes Teaching
                            </Header>
                            <Divider />
                            <List divided relaxed>
                                {Object.entries(
                                    user.coursesTeaching.reduce((acc: any, c: any) => {
                                        const sku = c.SKU.slice(-7);
                                        if (!acc[sku]) {
                                            acc[sku] = [];
                                        }
                                        acc[sku].push(c);
                                        return acc;
                                    }, {})
                                )
                                    .sort(sortBlocksDecending as any)
                                    .map(([sku, courses]: [string, any]) => (
                                        <List.Item key={sku}>
                                            <Header as="h4" style={{ marginBottom: '0.5em' }}>
                                                {sku}
                                            </Header>
                                            <List.List>
                                                {courses.map((course: any) => (
                                                    <List.Item key={course.id}>
                                                        <List.Content>
                                                            <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                                                                {course.name}
                                                            </List.Header>
                                                            <List.Description>
                                                                Teacher - {course.SKU}
                                                            </List.Description>
                                                        </List.Content>
                                                    </List.Item>
                                                ))}
                                            </List.List>
                                        </List.Item>
                                    ))}
                            </List>
                        </Segment>
                    )}

                    {/* --- Permissions Editor (if in edit mode) --- */}
                    {canEdit && !isCenterManager && (
                        <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                            <Header as="h2">
                                <Icon name="lock" /> Permissions
                            </Header>
                            <Divider />
                            <PermissionEditor
                                userId={Number(id)}
                                refetchPerson={() =>
                                    refetch({ variables: { personId: Number(id) } })
                                }
                            />
                        </Segment>
                    )}

                    {/* --- Archive Button (if in edit mode) --- */}
                    {canEdit && !isCenterManager && (
                        <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
                            <Header as="h2" color="red">
                                <Icon name="archive" /> Danger Zone
                            </Header>
                            <Divider />
                            <Button color="red" onClick={archiveUser}>
                                Archive Person
                            </Button>
                        </Segment>
                    )}
                </Container>
            </Segment>
        </ResponsiveContainer>
    );
};

export { AdminProfileView };
